import type { CSSProperties } from 'react'

/**
 * A serving dome, for lifting off a guest's finished answer about food.
 *
 * Drawn rather than photographed so it inherits the site's palette: a warm
 * pewter against the parchment instead of a cold silver that would look pasted
 * on. Purely decorative, so it is hidden from assistive technology - the answer
 * underneath is the content, and the summary already announces itself.
 */
export function Cloche() {
  return (
    <div aria-hidden="true" className="animate-cloche-lift pointer-events-none">
      <svg viewBox="0 0 140 96" className="h-auto w-full overflow-visible">
        <defs>
          <linearGradient id="cloche-dome" x1="0" y1="0" x2="0.7" y2="1">
            <stop offset="0%" stopColor="#efe7d6" />
            <stop offset="45%" stopColor="#dcd0b8" />
            <stop offset="100%" stopColor="#bfb198" />
          </linearGradient>
          <linearGradient id="cloche-plate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e6dcc8" />
            <stop offset="100%" stopColor="#c4b69c" />
          </linearGradient>
        </defs>

        {/* Steam first, so the dome covers where the wisps begin. */}
        <g stroke="#6b5d4d" strokeWidth="1.6" strokeLinecap="round" fill="none">
          {STEAM.map((wisp) => (
            <path
              key={wisp.d}
              d={wisp.d}
              className="animate-steam"
              style={{ '--steam-delay': `${wisp.delay}ms` } as CSSProperties}
            />
          ))}
        </g>

        {/* The dome, drawn from its base so the arc reads as a half-ellipse. */}
        <path
          d="M 18 74 A 52 50 0 0 1 122 74 Z"
          fill="url(#cloche-dome)"
          stroke="#6b5d4d"
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />
        {/* A single highlight sweep, which is most of what reads as metal. */}
        <path
          d="M 36 70 A 34 33 0 0 1 62 30"
          fill="none"
          stroke="#fdfaf3"
          strokeOpacity="0.75"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle
          cx="70"
          cy="21"
          r="5"
          fill="url(#cloche-dome)"
          stroke="#6b5d4d"
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />

        <ellipse
          cx="70"
          cy="76"
          rx="60"
          ry="7"
          fill="url(#cloche-plate)"
          stroke="#6b5d4d"
          strokeOpacity="0.3"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  )
}

/** Three wisps, offset so they do not pulse in unison. Scaled with the lift. */
const STEAM: ReadonlyArray<{ d: string; delay: number }> = [
  { d: 'M 58 18 q 5 -7 0 -13 q -5 -6 0 -12', delay: 300 },
  { d: 'M 70 12 q 6 -8 0 -15 q -6 -7 0 -13', delay: 0 },
  { d: 'M 82 18 q 5 -7 0 -13 q -5 -6 0 -12', delay: 600 },
]
